/**
 * El dataset completo del usuario, leído de IndexedDB y en vivo.
 *
 * Esto es lo que hace que ninguna pantalla necesite la red: `useLiveQuery` reemite cuando algo
 * cambia en local, así que registrar una serie la hace aparecer al instante, sin esperar al
 * servidor y sin un estado optimista paralelo que después haya que reconciliar.
 *
 * Es lógica COMPARTIDA por las dos variantes de UI. Si algo de acá termina copiado dentro de un
 * `.mobile.tsx` o de un `.desktop.tsx`, la regla 1 del proyecto se rompió.
 */

import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  hoyEnZona,
  type DiaBloqueado,
  type Ejercicio,
  type Fecha,
  type Gimnasio,
  type IngestaImagen,
  type Maquina,
  type MedidaCorporal,
  type Plan,
  type RegistroCardio,
  type Rutina,
  type RutinaDia,
  type RutinaEjercicio,
  type Alimento,
  type Serie,
  type SerieContexto,
  type Sesion,
} from '@mg/core';
import type { Perfil } from '@mg/sheets';

import { db } from '../datos/db.ts';

export interface Datos {
  cargando: boolean;
  perfil: Perfil | null;
  ejercicios: Ejercicio[];
  ejerciciosPorId: Map<string, Ejercicio>;
  gimnasios: Gimnasio[];
  maquinas: Maquina[];
  /** Máquina registrada para un ejercicio en el gimnasio activo, si alguien la mapeó. */
  maquinaDe: (ejercicioId: string) => Maquina | null;
  planes: Plan[];
  planActivo: Plan | null;
  gimnasioActivo: Gimnasio | null;
  diasBloqueados: DiaBloqueado[];
  rutinas: Rutina[];
  rutinaActiva: Rutina | null;
  rutinaDias: RutinaDia[];
  rutinaEjercicios: RutinaEjercicio[];
  /** Ejercicios de cada día de rutina, resueltos y en orden. */
  ejerciciosPorDia: Map<string, Ejercicio[]>;
  cantidadEjerciciosPorDia: Map<string, number>;
  sesiones: Sesion[];
  series: Serie[];
  cardio: RegistroCardio[];
  medidas: MedidaCorporal[];
  comidas: Alimento[];
  ingestas: IngestaImagen[];
  /** Series con su ejercicio y fecha resueltos: la entrada de todos los KPIs. */
  contextos: SerieContexto[];
  hoy: Fecha;
  sesionDeHoy: Sesion | null;
  sesionEnCurso: Sesion | null;
}

export function useDatos(): Datos {
  const bruto = useLiveQuery(async () => {
    const [
      perfil,
      ejercicios,
      gimnasios,
      maquinas,
      planes,
      diasBloqueados,
      rutinas,
      rutinaDias,
      rutinaEjercicios,
      sesiones,
      series,
      cardio,
      medidas,
      comidas,
      ingestas,
    ] = await Promise.all([
      db.perfil.toArray(),
      db.ejercicios.toArray(),
      db.gimnasios.toArray(),
      db.maquinas.toArray(),
      db.planes.toArray(),
      db.diasBloqueados.toArray(),
      db.rutinas.toArray(),
      db.rutinaDias.toArray(),
      db.rutinaEjercicios.toArray(),
      db.sesiones.toArray(),
      db.series.toArray(),
      db.cardio.toArray(),
      db.medidas.toArray(),
      db.comidas.toArray(),
      db.ingestas.toArray(),
    ]);

    return {
      perfil: perfil[0] ?? null,
      ejercicios,
      gimnasios,
      maquinas,
      planes,
      diasBloqueados,
      rutinas,
      rutinaDias,
      rutinaEjercicios,
      sesiones,
      series,
      cardio,
      medidas,
      comidas,
      ingestas,
    };
  }, []);

  return useMemo<Datos>(() => {
    if (!bruto) {
      return {
        cargando: true,
        perfil: null,
        ejercicios: [],
        ejerciciosPorId: new Map(),
        gimnasios: [],
        maquinas: [],
        maquinaDe: () => null,
        planes: [],
        planActivo: null,
        gimnasioActivo: null,
        diasBloqueados: [],
        rutinas: [],
        rutinaActiva: null,
        rutinaDias: [],
        rutinaEjercicios: [],
        ejerciciosPorDia: new Map(),
        cantidadEjerciciosPorDia: new Map(),
        sesiones: [],
        series: [],
        cardio: [],
        medidas: [],
        comidas: [],
        ingestas: [],
        contextos: [],
        hoy: hoyEnZona('America/Santiago'),
        sesionDeHoy: null,
        sesionEnCurso: null,
      };
    }

    const ejerciciosPorId = new Map(bruto.ejercicios.map((e) => [e.id, e]));
    const hoy = hoyEnZona(bruto.perfil?.zona_horaria ?? 'America/Santiago');

    // El plan activo es el que todavía no venció; si todos vencieron, el más reciente, para
    // poder mostrar "venció el 12 de septiembre" en vez de una pantalla vacía.
    const ordenadosPorVencimiento = [...bruto.planes].sort((a, b) =>
      a.fecha_vencimiento < b.fecha_vencimiento ? -1 : 1,
    );
    const planActivo =
      ordenadosPorVencimiento.find((p) => p.fecha_vencimiento >= hoy) ??
      ordenadosPorVencimiento[ordenadosPorVencimiento.length - 1] ??
      null;

    const gimnasioActivo = planActivo
      ? bruto.gimnasios.find((g) => g.id === planActivo.gimnasio_id) ?? bruto.gimnasios[0] ?? null
      : bruto.gimnasios[0] ?? null;

    const rutinaActiva = bruto.rutinas.find((r) => r.activa) ?? bruto.rutinas[0] ?? null;
    const diasDeRutina = bruto.rutinaDias
      .filter((d) => !rutinaActiva || d.rutina_id === rutinaActiva.id)
      .sort((a, b) => a.orden - b.orden);

    const ejerciciosPorDia = new Map<string, Ejercicio[]>();
    const cantidadEjerciciosPorDia = new Map<string, number>();
    for (const dia of diasDeRutina) {
      const lista = bruto.rutinaEjercicios
        .filter((re) => re.rutina_dia_id === dia.id)
        .sort((a, b) => a.orden - b.orden)
        .map((re) => ejerciciosPorId.get(re.ejercicio_id))
        .filter((e): e is Ejercicio => e !== undefined);
      ejerciciosPorDia.set(dia.id, lista);
      cantidadEjerciciosPorDia.set(dia.id, lista.length);
    }

    const fechaPorSesion = new Map(bruto.sesiones.map((s) => [s.id, s.fecha]));
    const contextos: SerieContexto[] = [];
    for (const serie of bruto.series) {
      const ejercicio = ejerciciosPorId.get(serie.ejercicio_id);
      const fecha = fechaPorSesion.get(serie.sesion_id);
      // Una serie huérfana (sin ejercicio o sin sesión en local) se omite en vez de romper el
      // cálculo: puede pasar en medio de un pull parcial.
      if (!ejercicio || !fecha) continue;
      contextos.push({ serie, ejercicio, fecha, sesion_id: serie.sesion_id });
    }

    const maquinasDelGimnasio = gimnasioActivo
      ? bruto.maquinas.filter((m) => m.gimnasio_id === gimnasioActivo.id)
      : bruto.maquinas;
    const maquinaPorEjercicio = new Map(maquinasDelGimnasio.map((m) => [m.ejercicio_id, m]));

    return {
      cargando: false,
      perfil: bruto.perfil,
      ejercicios: bruto.ejercicios,
      ejerciciosPorId,
      gimnasios: bruto.gimnasios,
      maquinas: bruto.maquinas,
      maquinaDe: (ejercicioId: string) => maquinaPorEjercicio.get(ejercicioId) ?? null,
      planes: bruto.planes,
      planActivo,
      gimnasioActivo,
      diasBloqueados: bruto.diasBloqueados,
      rutinas: bruto.rutinas,
      rutinaActiva,
      rutinaDias: diasDeRutina,
      rutinaEjercicios: bruto.rutinaEjercicios,
      ejerciciosPorDia,
      cantidadEjerciciosPorDia,
      sesiones: [...bruto.sesiones].sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0)),
      series: bruto.series,
      cardio: bruto.cardio,
      medidas: [...bruto.medidas].sort((a, b) => (a.fecha < b.fecha ? -1 : 1)),
      comidas: bruto.comidas,
      ingestas: bruto.ingestas,
      contextos,
      hoy,
      sesionDeHoy: bruto.sesiones.find((s) => s.fecha === hoy) ?? null,
      sesionEnCurso: bruto.sesiones.find((s) => s.estado === 'en_curso') ?? null,
    };
  }, [bruto]);
}
